import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import _ from 'lodash';
import ItemLeadTimeAPI, {
  SubmitItemLeadTimeData,
  SubmitBulkItemLeadTimeData,
  ItemLeadTimePaginationParams,
  PaginatedItemLeadTimeResponse,
  SortDirection,
} from '../Api/item-lead-time';
import {
  EditedItemLeadTime,
  ItemLeadTime,
  CompletedItemLeadTime,
} from '../../Components/RequestedItems/types';
import { normalizeSubmissionData } from '../../Components/RequestedItems/utils/normalizeSubmissionData';

export interface UseItemLeadTimesParams extends ItemLeadTimePaginationParams {
  userId: string;
  sort?: SortDirection;
  enabled?: boolean;
}

export const useGetTodoUserItemLeadTimes = ({
  userId,
  page = 1,
  limit = 50,
  sort,
  search,
  enabled = true,
}: UseItemLeadTimesParams) =>
  useQuery<PaginatedItemLeadTimeResponse<ItemLeadTime>>(
    ['todo-item-lead-times', userId, { page, limit, sort, search }],
    () =>
      ItemLeadTimeAPI.getTodoItemLeadTimes(userId, {
        page,
        limit,
        sort,
        search,
      }),
    {
      enabled,
    },
  );

export const useGetOpenChatItemLeadTimes = ({
  userId,
  page = 1,
  limit = 50,
  sort,
  search,
  enabled = true,
}: UseItemLeadTimesParams) =>
  useQuery<PaginatedItemLeadTimeResponse<ItemLeadTime>>(
    ['open-chat-item-lead-times', userId, { page, limit, sort, search }],
    () =>
      ItemLeadTimeAPI.getOpenChatItemLeadTimes(userId, {
        page,
        limit,
        sort,
        search,
      }),
    { enabled: enabled && !!userId },
  );

export const useGetCompletedUserItemLeadTimes = ({
  userId,
  page = 1,
  limit = 50,
  sort,
  search,
}: UseItemLeadTimesParams) =>
  useQuery<PaginatedItemLeadTimeResponse<CompletedItemLeadTime>>(
    ['completed-item-lead-times', userId, { page, limit, sort, search }],
    () =>
      ItemLeadTimeAPI.getCompletedItemLeadTimes(userId, {
        page,
        limit,
        sort,
        search,
      }),
  );

type SubmitItemLeadTimeParams = {
  userId: string;
  worksheetId: string;
  data: SubmitItemLeadTimeData;
};

type SubmitBulkItemLeadTimesParams = {
  userId: string;
  items: SubmitBulkItemLeadTimeData[];
};

export const useSubmitItemLeadTime = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: SubmitItemLeadTimeParams) =>
      ItemLeadTimeAPI.submitItemLeadTime(
        params.userId,
        params.worksheetId,
        params.data,
      ),
    {
      onSuccess: async (_data, variables) => {
        await queryClient.cancelQueries([
          'todo-item-lead-times',
          variables.userId,
        ]);
        await queryClient.invalidateQueries([
          'todo-item-lead-times',
          variables.userId,
        ]);
        await queryClient.invalidateQueries([
          'open-chat-item-lead-times',
          variables.userId,
        ]);
        await queryClient.invalidateQueries([
          'completed-item-lead-times',
          variables.userId,
        ]);
        toast('Item lead time submitted', { type: 'success' });
      },
      onError: () => {
        toast('Failed to submit item lead time', { type: 'error' });
      },
    },
  );
};

type AddItemLeadTimeCommentData = {
  worksheet_id: string;
  comments: string;
};

type AddItemLeadTimeCommentParams = {
  userId: string;
  data: AddItemLeadTimeCommentData;
};

export const useAddItemLeadTimeComment = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (params: AddItemLeadTimeCommentParams) =>
      ItemLeadTimeAPI.addItemLeadTimeComment(params.userId, params.data),
    {
      onSuccess: async (_data, variables) => {
        await queryClient.invalidateQueries([
          'completed-item-lead-times',
          variables.userId,
        ]);
        toast('Item lead time comments updated', { type: 'success' });
      },
      onError: () => {
        toast('Failed to update item lead time comments', { type: 'error' });
      },
    },
  );
};

export const useSubmitBulkItemLeadTimes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SubmitBulkItemLeadTimesParams) =>
      ItemLeadTimeAPI.bulkUpdateItemLeadTimes(input.userId, input.items),
    onSuccess: async (_data, variables) => {
      await queryClient.cancelQueries([
        'todo-item-lead-times',
        variables.userId,
      ]);
      await queryClient.invalidateQueries([
        'todo-item-lead-times',
        variables.userId,
      ]);
      await queryClient.invalidateQueries([
        'open-chat-item-lead-times',
        variables.userId,
      ]);
      await queryClient.invalidateQueries([
        'completed-item-lead-times',
        variables.userId,
      ]);
      toast(`${variables.items.length} items submitted. Thank you !`, {
        type: 'success',
      });
    },
    onError: () => {
      toast('Failed to submit all item lead times', { type: 'error' });
    },
  });
};

type SaveItemLeadTimesParams = {
  userId: string;
  items: EditedItemLeadTime[];
};

export const useSaveItemLeadTimes = () =>
  useMutation({
    mutationFn: (input: SaveItemLeadTimesParams) =>
      ItemLeadTimeAPI.saveItemLeadTimes(
        input.userId,
        input.items.map((item) =>
          normalizeSubmissionData(
            _.pick(item, [
              'earliest_custom_cut_delivery_date',
              'country_of_origin',
              'cost_jr',
              'cost_plus',
              'exclusivity',
              'stock_quantity',
              'stock_ratio',
              'earliest_IHD_date_to_deliver_stock',
              'custom_cut_lead_time_in_weeks',
              'incoming_stock_quantity',
              'incoming_stock_date',
              'incoming_stock_ratio',
              'custom_MOQ_jr',
              'custom_MOQ_plus',
              'shipment_mode',
              'comments',
              'worksheet_id',
            ]),
          ),
        ) as SubmitBulkItemLeadTimeData[],
      ),
  });
